import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useOutlet } from 'react-router-dom';
import type { ClockIconHandle } from '../components/ui/clock';
import type { FileTextIconHandle } from '../components/ui/file-text';
import type { HomeIconHandle } from '../components/ui/home';
import type { SettingsIconHandle } from '../components/ui/settings';
import { useScrollStore } from '../stores/scroll';

// Tab routes that use horizontal slide animation
export const tabRoutes = ['/', '/reports', '/templates', '/settings'] as const;
export type TabRoute = (typeof tabRoutes)[number];

export const routeOrder: { [key: string]: number } = {
  '/': 0,
  '/reports': 1,
  '/templates': 2,
  '/settings': 3,
};

// Animation variants for tab switching (fade)
export const tabVariants = {
  enter: {
    opacity: 0,
  },
  center: {
    zIndex: 1,
    opacity: 1,
  },
  exit: {
    zIndex: 0,
    opacity: 0,
  },
};

// Animation variants for page transitions (slide and fade up)
export const pageVariants = {
  enter: {
    opacity: 0,
  },
  center: {
    zIndex: 1,
    opacity: 1,
  },
  exit: {
    zIndex: 0,
    opacity: 0,
  },
};

export const transition = {
  type: 'spring' as const,
  duration: 0.12,
  bounce: 0,
};

export function useNavigation() {
  const location = useLocation();
  const outlet = useOutlet();
  const navigate = useNavigate();
  const prevLocationIndexRef = useRef(routeOrder[location.pathname] ?? 0);
  const prevPathnameRef = useRef(location.pathname);
  const [isAnimating, setIsAnimating] = useState(false);
  const homeIconRef = useRef<HomeIconHandle>(null);
  const clockIconRef = useRef<ClockIconHandle>(null);
  const fileTextIconRef = useRef<FileTextIconHandle>(null);
  const settingsIconRef = useRef<SettingsIconHandle>(null);
  const bottomNavRef = useRef<HTMLDivElement>(null);
  const [bottomNavHeight, setBottomNavHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      if (bottomNavRef.current) {
        const height = bottomNavRef.current.offsetHeight;
        setBottomNavHeight(height);
      }
    };
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(updateHeight);
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Get base route for route ordering (handles sub-routes)
  const getBaseRoute = (pathname: string): string => {
    // Check if it's a direct tab route
    if ((tabRoutes as readonly string[]).includes(pathname)) {
      return pathname as TabRoute;
    }
    // For sub-routes, find the matching tab route
    for (const tabRoute of tabRoutes) {
      if (
        pathname.startsWith(`${tabRoute}/`) ||
        pathname.startsWith(`${tabRoute}?`)
      ) {
        return tabRoute;
      }
    }
    return pathname;
  };

  const baseRoute = getBaseRoute(location.pathname);
  const prevBaseRoute = getBaseRoute(prevPathnameRef.current);
  const currentRouteIndex = routeOrder[baseRoute] ?? 0;

  // Check if we're navigating between different tab routes (tab switching)
  // vs navigating within the same tab or to/from sub-routes (fade up)
  const isTabSwitching =
    baseRoute !== prevBaseRoute &&
    (tabRoutes as readonly string[]).includes(baseRoute) &&
    (tabRoutes as readonly string[]).includes(prevBaseRoute);

  useEffect(() => {
    prevLocationIndexRef.current = currentRouteIndex;
    prevPathnameRef.current = location.pathname;
  }, [currentRouteIndex, location.pathname]);

  // Reset scroll-aware border when navigating to a new page (new page starts at top)
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is intentionally the trigger for reset
  useEffect(() => {
    useScrollStore.getState().setIsContentScrolled(false);
  }, [location.pathname]);

  const handleNavigate = (path: string, withoutAnimation = false) => {
    if (isAnimating || location.pathname === path) return;
    if (!withoutAnimation) {
      setIsAnimating(true);
    }
    navigate(path);
  };

  const isActive = (path: string) => {
    // For templates, check if pathname starts with /templates
    if (path === '/templates') {
      return (
        location.pathname === '/templates' ||
        location.pathname.startsWith('/templates/')
      );
    }
    return location.pathname === path;
  };

  const stopAllAnimations = (
    ignore: 'home' | 'reports' | 'templates' | 'settings',
  ) => {
    if (ignore !== 'home') {
      homeIconRef.current?.stopAnimation();
    }
    if (ignore !== 'reports') {
      clockIconRef.current?.stopAnimation();
    }
    if (ignore !== 'templates') {
      fileTextIconRef.current?.stopAnimation();
    }
    if (ignore !== 'settings') {
      settingsIconRef.current?.stopAnimation();
    }
  };

  return {
    location,
    outlet,
    navigate,
    isAnimating,
    setIsAnimating,
    isTabSwitching,
    handleNavigate,
    isActive,
    homeIconRef,
    clockIconRef,
    fileTextIconRef,
    settingsIconRef,
    bottomNavRef,
    bottomNavHeight,
    stopAllAnimations,
  };
}
